import React, { useEffect, useState } from 'react';
import { autoRechargeService } from '../../api/services/autorecharge.service';
import { GatewayAccountDTO, GatewayAccountUpsert, GatewayType } from '../../types/autorecharge';
import { formatServerUtcAsIst } from './closeBooksRules';
import { SSM_PREFIX, gatewayNames, modeLabel, validateMarket, validateParamName } from './autoRechargeRules';

/**
 * Billing → Payment Gateways (master only). Merchant accounts belong to the ISSUING
 * organisation (Teeoff SG, Teeoff US, Green Giraffe India, …). Secrets are never typed
 * here: only the SSM parameter NAMES that hold them. "Test connection" works in test mode.
 */

interface FormState {
  id: number | null;
  ownerOrganizationId: string;
  gateway: GatewayType;
  label: string;
  countries: string;
  currencies: string;
  mode: string;
  keyIdParam: string;
  keySecretParam: string;
  webhookSecretParam: string;
  enabled: boolean;
  priority: string;
}

const blank: FormState = {
  id: null,
  ownerOrganizationId: '',
  gateway: 'RAZORPAY',
  label: '',
  countries: 'IN',
  currencies: 'INR',
  mode: 'test',
  keyIdParam: SSM_PREFIX,
  keySecretParam: SSM_PREFIX,
  webhookSecretParam: SSM_PREFIX,
  enabled: false,
  priority: '100',
};

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap text-left';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const btn = 'px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200';

export const formErrors = (f: FormState): string[] => {
  const errs: string[] = [];
  if (!f.ownerOrganizationId.trim()) errs.push('Owner organization is required');
  if (!f.label.trim()) errs.push('Label is required');
  const market = validateMarket(f.gateway, f.countries, f.currencies);
  if (market) errs.push(market);
  const k = validateParamName(f.keyIdParam === SSM_PREFIX ? '' : f.keyIdParam, f.gateway === 'RAZORPAY');
  if (k) errs.push(`Key id parameter: ${k}`);
  const s = validateParamName(f.keySecretParam === SSM_PREFIX ? '' : f.keySecretParam, true);
  if (s) errs.push(`Key secret parameter: ${s}`);
  const w = validateParamName(f.webhookSecretParam === SSM_PREFIX ? '' : f.webhookSecretParam, true);
  if (w) errs.push(`Webhook secret parameter: ${w}`);
  if (!/^\d+$/.test(f.priority)) errs.push('Priority must be a number');
  return errs;
};

const toBody = (f: FormState): GatewayAccountUpsert => ({
  ownerOrganizationId: f.ownerOrganizationId.trim(),
  gateway: f.gateway,
  label: f.label.trim(),
  countries: f.countries,
  currencies: f.currencies,
  mode: f.mode,
  keyIdParam: f.keyIdParam && f.keyIdParam !== SSM_PREFIX ? f.keyIdParam.trim() : undefined,
  keySecretParam: f.keySecretParam.trim(),
  webhookSecretParam: f.webhookSecretParam.trim(),
  enabled: f.enabled,
  priority: Number(f.priority),
});

const PaymentGateways: React.FC = () => {
  const [rows, setRows] = useState<GatewayAccountDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<number, string>>({});

  const load = async () => {
    const r = await autoRechargeService.listGateways();
    if (r.success && r.data) {
      setRows(r.data);
      setError(null);
    } else {
      setError(r.error || 'Failed to load');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const edit = (a: GatewayAccountDTO) =>
    setForm({
      id: a.id,
      ownerOrganizationId: a.ownerOrganizationId,
      gateway: a.gateway,
      label: a.label,
      countries: a.countries,
      currencies: a.currencies,
      mode: a.mode.toLowerCase(),
      keyIdParam: a.keyIdParam || '',
      keySecretParam: a.keySecretParam,
      webhookSecretParam: a.webhookSecretParam,
      enabled: a.enabled,
      priority: String(a.priority),
    });

  const save = async () => {
    if (!form) return;
    const errs = formErrors(form);
    if (errs.length) {
      setSaveError(errs.join('. '));
      return;
    }
    const r = form.id === null
      ? await autoRechargeService.createGateway(toBody(form))
      : await autoRechargeService.updateGateway(form.id, toBody(form));
    if (r.success) {
      setForm(null);
      setSaveError(null);
      load();
    } else {
      setSaveError(r.error || 'Failed to save');
    }
  };

  const test = async (a: GatewayAccountDTO) => {
    const r = await autoRechargeService.testGateway(a.id);
    const text = r.success && r.data
      ? `${r.data.ok ? 'OK' : 'Failed'}: ${r.data.message}${r.data.webhookSecretReadable === false ? ' (webhook secret NOT readable)' : ''}`
      : r.error || 'Failed';
    setTestResult({ ...testResult, [a.id]: text });
  };

  const set = (patch: Partial<FormState>) => form && setForm({ ...form, ...patch });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Gateways</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Merchant accounts of the organisations that invoice customers. India/INR uses Razorpay; everyone else Stripe.
            Secrets live in SSM — enter parameter names only.
          </p>
        </div>
        <button className="px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600" onClick={() => setForm({ ...blank })}>
          Add gateway account
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className={th}>Owner</th>
              <th className={th}>Gateway</th>
              <th className={th}>Label</th>
              <th className={th}>Serves</th>
              <th className={th}>Mode (effective)</th>
              <th className={th}>Webhook URL path</th>
              <th className={th}>Enabled</th>
              <th className={th}>Updated (IST)</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td className={td}>{a.ownerName || a.ownerOrganizationId}</td>
                <td className={td}>{gatewayNames[a.gateway]}</td>
                <td className={td}>{a.label}</td>
                <td className={td}>{a.countries} · {a.currencies}</td>
                <td className={td} title={`account ${a.mode}, platform ${a.platformMode}`}>{modeLabel(a.effectiveMode)}</td>
                <td className={td}><code>{a.webhookPath}</code></td>
                <td className={td}>{a.enabled ? 'Yes' : 'No'}</td>
                <td className={td}>{formatServerUtcAsIst(a.updatedAt)}</td>
                <td className={td}>
                  <div className="flex gap-2 items-center">
                    <button className={btn} onClick={() => edit(a)}>Edit</button>
                    <button className={btn} onClick={() => test(a)} disabled={a.effectiveMode !== 'TEST'}
                      title={a.effectiveMode !== 'TEST' ? 'Only in test mode' : ''}>
                      Test connection
                    </button>
                    {testResult[a.id] && <span className="text-xs text-gray-600 dark:text-gray-300">{testResult[a.id]}</span>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !error && (
              <tr><td className={td} colSpan={9}>No gateway accounts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {form.id === null ? 'Add gateway account' : `Edit ${form.label}`}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="pg-owner">Owner (issuing) organization id</label>
                <input id="pg-owner" className={inputCls} value={form.ownerOrganizationId} onChange={(e) => set({ ownerOrganizationId: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="pg-gateway">Gateway</label>
                <select id="pg-gateway" className={inputCls} value={form.gateway} onChange={(e) => {
                  const g = e.target.value as GatewayType;
                  set(g === 'RAZORPAY' ? { gateway: g, countries: 'IN', currencies: 'INR' } : { gateway: g, countries: '*', currencies: 'USD' });
                }}>
                  <option value="RAZORPAY">Razorpay (India, INR)</option>
                  <option value="STRIPE">Stripe (outside India)</option>
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="pg-label">Label</label>
                <input id="pg-label" className={inputCls} value={form.label} placeholder="e.g. Teeoff SG" onChange={(e) => set({ label: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="pg-mode">Mode</label>
                <select id="pg-mode" className={inputCls} value={form.mode} onChange={(e) => set({ mode: e.target.value })}>
                  <option value="off">Off</option>
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="pg-countries">Countries (ISO-2, comma; * = any)</label>
                <input id="pg-countries" className={inputCls} value={form.countries} onChange={(e) => set({ countries: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="pg-currencies">Currencies</label>
                <input id="pg-currencies" className={inputCls} value={form.currencies} onChange={(e) => set({ currencies: e.target.value })} />
              </div>
              {form.gateway === 'RAZORPAY' && (
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="pg-keyid">Key id — SSM parameter name</label>
                  <input id="pg-keyid" className={inputCls} value={form.keyIdParam} onChange={(e) => set({ keyIdParam: e.target.value })} />
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="pg-secret">{form.gateway === 'RAZORPAY' ? 'Key secret' : 'Secret key'} — SSM parameter name</label>
                <input id="pg-secret" className={inputCls} value={form.keySecretParam} onChange={(e) => set({ keySecretParam: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="pg-webhook">Webhook secret — SSM parameter name</label>
                <input id="pg-webhook" className={inputCls} value={form.webhookSecretParam} onChange={(e) => set({ webhookSecretParam: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="pg-priority">Priority (lower wins)</label>
                <input id="pg-priority" className={inputCls} value={form.priority} onChange={(e) => set({ priority: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 mt-6">
                <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled
              </label>
            </div>
            {saveError && <p className="text-sm text-red-600">{saveError}</p>}
            <div className="flex justify-end gap-2">
              <button className={btn} onClick={() => { setForm(null); setSaveError(null); }}>Cancel</button>
              <button className="px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600" onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGateways;
